import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Trash2, Save, Upload, X } from "lucide-react";
import { clientAPI } from "@/lib/client-api";
import type { GalleryImage, GallerySection } from "@/lib/db-api";

const AdminGallery = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [section, setSection] = useState<GallerySection | null>(null);
  const [editingImage, setEditingImage] = useState<GalleryImage | null>(null);
  const [newImage, setNewImage] = useState({ image: "", alt_text: "" });
  const [uploadPreview, setUploadPreview] = useState<string>("");
  const [editUploadPreview, setEditUploadPreview] = useState<string>("");

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth !== 'true') {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = () => {
    try {
      const imageData = clientAPI.getGalleryImages();
      const sectionData = clientAPI.getGallerySection();
      setImages(imageData);
      if (sectionData) setSection(sectionData);
    } catch (error) {
      console.error('Error loading gallery:', error);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      if (isEdit && editingImage) {
        setEditingImage({ ...editingImage, image: base64String });
        setEditUploadPreview(base64String);
      } else {
        setNewImage({ ...newImage, image: base64String });
        setUploadPreview(base64String);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearUpload = (isEdit = false) => {
    if (isEdit && editingImage) {
      setEditingImage({ ...editingImage, image: "" });
      setEditUploadPreview("");
    } else {
      setNewImage({ ...newImage, image: "" });
      setUploadPreview("");
    }
  };


  const handleAddImage = () => {
    if (!newImage.image) {
      toast({
        title: "Error",
        description: "Please provide an image",
        variant: "destructive",
      });
      return;
    }

    try {
      clientAPI.createGalleryImage({
        ...newImage,
        order_index: images.length + 1,
        active: 1,
      });
      setNewImage({ image: "", alt_text: "" });
      setUploadPreview("");
      loadData();
      toast({
        title: "Success",
        description: "Image added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add image",
        variant: "destructive",
      });
    }
  };

  const handleUpdateImage = () => {
    if (!editingImage) return;

    try {
      clientAPI.updateGalleryImage(editingImage.id, editingImage);
      setEditingImage(null);
      setEditUploadPreview("");
      loadData();
      toast({
        title: "Success",
        description: "Image updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update image",
        variant: "destructive",
      });
    }
  };

  const handleDeleteImage = (id: number) => {
    if (confirm("Are you sure you want to delete this image?")) {
      try {
        clientAPI.deleteGalleryImage(id);
        loadData();
        toast({
          title: "Success",
          description: "Image deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete image",
          variant: "destructive",
        });
      }
    }
  };

  const handleSaveSection = () => {
    if (!section) return;

    try {
      clientAPI.updateGallerySection(section);
      toast({
        title: "Success",
        description: "Gallery section updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update gallery section",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-6">
          {section && (
            <Card>
              <CardHeader>
                <CardTitle>Gallery Section Content</CardTitle>
                <CardDescription>Edit the gallery section title and description</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sectionTitle">Section Title</Label>
                  <Input
                    id="sectionTitle"
                    value={section.title}
                    onChange={(e) => setSection({ ...section, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sectionDescription">Section Description</Label>
                  <Textarea
                    id="sectionDescription"
                    value={section.description}
                    onChange={(e) => setSection({ ...section, description: e.target.value })}
                    rows={2}
                  />
                </div>
                <Button onClick={handleSaveSection}>
                  <Save className="mr-2 h-4 w-4" />
                  Save Section
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Add New Gallery Image</CardTitle>
              <CardDescription>Add a new image to the gallery carousel</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="image">Image Upload</Label>
                <div className="space-y-2">
                  {uploadPreview && (
                    <div className="relative">
                      <img 
                        src={uploadPreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => clearUpload()}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e)}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground">Upload an image (max 5MB) or use the URL field below</p>
                  <Input
                    id="image"
                    value={newImage.image.startsWith('data:') ? '' : newImage.image}
                    onChange={(e) => setNewImage({ ...newImage, image: e.target.value })}
                    placeholder="Or paste image URL"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="alt">Alt Text (Optional)</Label>
                <Input
                  id="alt"
                  value={newImage.alt_text}
                  onChange={(e) => setNewImage({ ...newImage, alt_text: e.target.value })}
                  placeholder="Gallery image description"
                />
              </div>
              <Button onClick={handleAddImage}>
                <Plus className="mr-2 h-4 w-4" />
                Add Image
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Gallery Images</CardTitle>
              <CardDescription>Manage your gallery images</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {images.map((image) => (
                  <div key={image.id} className="border p-4 rounded-lg space-y-4">
                    {editingImage?.id === image.id ? (
                      <>
                        <div className="space-y-2">
                          <Label>Image</Label>
                          <div className="space-y-2">
                            {editUploadPreview || editingImage.image ? (
                              <div className="relative">
                                <img 
                                  src={editUploadPreview || editingImage.image} 
                                  alt="Preview" 
                                  className="w-full h-48 object-cover rounded-lg"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="absolute top-2 right-2"
                                  onClick={() => clearUpload(true)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : null}
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, true)}
                              className="cursor-pointer"
                            />
                            <p className="text-xs text-muted-foreground">Upload an image (max 5MB) or use the URL field below</p>
                            <Input
                              value={editingImage.image.startsWith('data:') ? '' : editingImage.image}
                              onChange={(e) => setEditingImage({ ...editingImage, image: e.target.value })}
                              placeholder="Or paste image URL"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Alt Text</Label>
                          <Input
                            value={editingImage.alt_text || ""}
                            onChange={(e) => setEditingImage({ ...editingImage, alt_text: e.target.value })}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleUpdateImage}>
                            <Save className="mr-2 h-4 w-4" />
                            Save
                          </Button>
                          <Button variant="outline" onClick={() => setEditingImage(null)}>
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <img 
                            src={image.image} 
                            alt={image.alt_text || "Gallery image"} 
                            className="w-full h-48 object-cover rounded-lg"
                          />
                          {image.alt_text && (
                            <p className="text-sm text-muted-foreground">Alt: {image.alt_text}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setEditingImage(image)}>
                            Edit
                          </Button>
                          <Button variant="destructive" onClick={() => handleDeleteImage(image.id)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminGallery;
